import _taggedTemplateLiteral from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/taggedTemplateLiteral.js";

var _templateObject;

import { PageFilledIcon } from '@infl-ts/ui-advocate-icons';
import { ignoreQuartersFormatDistance } from '@infl-ts/util-date';
import { useLanguage } from '@infl-ts/util-i18n-provider';
import { Box, Button, List, ListItem, styled, Typography } from '@mui/material';
import { parseISO } from 'date-fns';
import { absurd, pipe } from 'fp-ts/function';
import * as O from 'fp-ts/Option';
import { Trans, Translation } from 'react-i18next';
import URI from 'urijs';
import { DangerouslySetInnerHTML } from '@infl-ts/ui-advocate-components';
import { Avatar } from '../avatar';
import { ClientSideRender } from '../client-side-render';
import { ExternalLink } from '../external-link';
import { Link } from '../link';
import { RouterLink } from '../router-link';
import { TypographyTruncate } from '../typography-truncate';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";

var thumbnailUrl = function thumbnailUrl(url) {
  var uri = new URI(url);

  if (uri.hostname() === 'cdn.filestackcontent.com') {
    var handle = uri.path();
    return URI.joinPaths('/filestack', handle).toString();
  } else {
    return url;
  }
};

var filename = function filename(url) {
  return new URI(url).fragment();
};

var previewUrl = function previewUrl(url) {
  if (!/(pdf|pps|pptx?|xlsx?|docx?|odt|odp|ods|txt|rtf)$/.test(url)) return O.none;
  var uri = new URI(url);
  var handle = uri.path();

  if (uri.hostname() === 'cdn.filestackcontent.com') {
    return O.some(URI.joinPaths('/preview', handle).toString());
  } else {
    return O.some(URI.joinPaths('/preview', handle.replace('/filestack', '')).toString());
  }
};

var downloadUrl = function downloadUrl(url) {
  var uri = new URI(url);

  if (uri.hostname() === 'cdn.filestackcontent.com') {
    var handle = uri.path();
    return URI.joinPaths('/filestack', handle, '/download').toString();
  } else {
    return uri.fragment('') + '/download';
  }
};

export var Video = styled('video')(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  max-width: 100%;\n  max-height: 270px;\n  object-fit: contain;\n"])));
export var LinkedImage = function LinkedImage(_ref) {
  var url = _ref.url,
      alt = _ref.alt,
      _ref$popover = _ref.popover,
      popover = _ref$popover === void 0 ? false : _ref$popover;
  return /*#__PURE__*/_jsx(ExternalLink, {
    href: url,
    target: "_blank",
    rel: "noopener noreferrer",
    onClick: function onClick(e) {
      return e.stopPropagation();
    },
    sx: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: popover ? 'center' : 'flex-start'
    },
    "data-element": "ExternalLink",
    "data-component": "LinkedImage",
    "data-source-file": "popover-body.tsx",
    children: /*#__PURE__*/_jsx(Box, {
      component: "img",
      sx: {
        width: popover ? 1 : 'auto',
        maxWidth: popover ? 204 : '100%',
        maxHeight: 204,
        objectFit: 'contain'
      },
      src: url,
      alt: alt,
      "data-element": "Box",
      "data-component": "LinkedImage",
      "data-source-file": "popover-body.tsx"
    })
  });
};

var FilePreview = function FilePreview(_ref2) {
  var url = _ref2.url;
  return /*#__PURE__*/_jsxs(Box, {
    sx: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    },
    "data-element": "Box",
    "data-component": "FilePreview",
    "data-source-file": "popover-body.tsx",
    children: [pipe(previewUrl(url), O.match(function () {
      return /*#__PURE__*/_jsx(PageFilledIcon, {});
    }, function () {
      return /*#__PURE__*/_jsx(LinkedImage, {
        url: thumbnailUrl(url),
        alt: "uploaded file",
        popover: true
      });
    })), /*#__PURE__*/_jsxs(Box, {
      sx: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      },
      "data-element": "Box",
      "data-component": "FilePreview",
      "data-source-file": "popover-body.tsx",
      children: [filename(url), /*#__PURE__*/_jsxs(Box, {
        sx: {
          display: 'flex',
          justifyContent: 'center'
        },
        "data-element": "Box",
        "data-component": "FilePreview",
        "data-source-file": "popover-body.tsx",
        children: [pipe(previewUrl(url), O.map(function (url) {
          return /*#__PURE__*/_jsx(ExternalLink, {
            sx: {
              ml: 0,
              mr: 2
            },
            href: url,
            target: "_blank",
            rel: "noopener noreferrer",
            children: "View"
          }, url);
        }), O.toNullable), /*#__PURE__*/_jsx(ExternalLink, {
          sx: {
            ml: 0
          },
          href: downloadUrl(url),
          target: "_blank",
          rel: "noopener noreferrer",
          "data-element": "ExternalLink",
          "data-component": "FilePreview",
          "data-source-file": "popover-body.tsx",
          children: "Download"
        })]
      })]
    })]
  });
};

var ChallengeResponse = function ChallengeResponse(_ref3) {
  var answer = _ref3.answer,
      answerType = _ref3.answerType;
  if (answerType === 'Image') return /*#__PURE__*/_jsx(LinkedImage, {
    url: answer,
    alt: "uploaded image",
    popover: true
  });
  if (answerType === 'ConvertedVideo') return /*#__PURE__*/_jsx(Video, {
    src: answer,
    controls: true
  });

  if (answerType === 'RawVideo' || answerType === 'BrokenVideo') {
    return /*#__PURE__*/_jsx(ExternalLink, {
      href: answer,
      target: "_blank",
      rel: "noopener noreferrer",
      children: "Uploaded a video"
    });
  }

  if (answerType === 'File') return /*#__PURE__*/_jsx(FilePreview, {
    url: answer
  });

  if (answerType === 'Text') {
    return /*#__PURE__*/_jsx(Box, {
      sx: {
        width: 1,
        m: 0
      },
      children: /*#__PURE__*/_jsx(TypographyTruncate, {
        lines: 2,
        variant: "body1",
        children: answer
      })
    });
  }

  return absurd(answerType);
};

var Timestamp = function Timestamp(_ref4) {
  var createdAt = _ref4.createdAt;

  var _useLanguage = useLanguage(),
      currentLang = _useLanguage.currentLang;

  return /*#__PURE__*/_jsx(ClientSideRender, {
    "data-element": "ClientSideRender",
    "data-component": "Timestamp",
    "data-source-file": "popover-body.tsx",
    children: /*#__PURE__*/_jsx(Typography, {
      component: "span",
      variant: "body2",
      sx: {
        color: function color(t) {
          return t.palette.text.secondary;
        }
      },
      "data-element": "Typography",
      "data-component": "Timestamp",
      "data-source-file": "popover-body.tsx",
      children: createdAt ? ignoreQuartersFormatDistance(parseISO(createdAt), Date.now(), currentLang) : ''
    })
  });
};

var PopoverBody = function PopoverBody(_ref5) {
  var _responses$length;

  var _ref5$response = _ref5.response,
      member = _ref5$response.member,
      responses = _ref5$response.responses,
      createdAt = _ref5$response.createdAt,
      responseLink = _ref5.responseLink,
      isResponseOpenable = _ref5.isResponseOpenable;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Box, {
      sx: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%'
      },
      "data-element": "Box",
      "data-component": "PopoverBody",
      "data-source-file": "popover-body.tsx",
      children: /*#__PURE__*/_jsxs(Box, {
        sx: {
          display: 'flex',
          alignItems: 'center',
          width: '100%'
        },
        "data-element": "Box",
        "data-component": "PopoverBody",
        "data-source-file": "popover-body.tsx",
        children: [/*#__PURE__*/_jsx(Avatar, {
          disabledPopover: true,
          member: member,
          sx: {
            mr: 2
          },
          "data-element": "Avatar",
          "data-component": "PopoverBody",
          "data-source-file": "popover-body.tsx"
        }), /*#__PURE__*/_jsxs(Box, {
          sx: {
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1
          },
          "data-element": "Box",
          "data-source-file": "popover-body.tsx",
          children: [/*#__PURE__*/_jsx(Typography, {
            component: "span",
            variant: "body2",
            "data-element": "Typography",
            "data-source-file": "popover-body.tsx",
            children: pipe((_responses$length = responses === null || responses === void 0 ? void 0 : responses.length) !== null && _responses$length !== void 0 ? _responses$length : 0, function (numberOfQuestions) {
              return /*#__PURE__*/_jsx(Trans, {
                i18nKey: "answeredQuestionsText",
                values: {
                  name: member.name
                },
                components: {
                  l: /*#__PURE__*/_jsx(Link, {
                    variant: "h5",
                    sx: {
                      pr: 0.5
                    },
                    to: "/profiles/".concat(member.id)
                  })
                },
                count: numberOfQuestions
              });
            })
          }), /*#__PURE__*/_jsx(Timestamp, {
            createdAt: createdAt,
            "data-element": "Timestamp",
            "data-source-file": "popover-body.tsx"
          })]
        }), isResponseOpenable && /*#__PURE__*/_jsx(Button, {
          sx: {
            mx: 1,
            minWidth: 'fit-content',
            color: function color(t) {
              return t.palette.primary.contrastText;
            }
          },
          component: RouterLink,
          variant: "contained",
          to: responseLink,
          children: /*#__PURE__*/_jsx(Translation, {
            children: function children(t) {
              return t('Go to response');
            }
          })
        })]
      })
    }), responses && /*#__PURE__*/_jsx(List, {
      disablePadding: true,
      children: responses.map(function (_ref6, index) {
        var question = _ref6.question,
            answer = _ref6.answer,
            answerType = _ref6.answerType;
        return /*#__PURE__*/_jsxs(ListItem, {
          disableGutters: true,
          alignItems: "flex-start",
          sx: {
            flexDirection: 'column',
            mt: 2,
            p: 0
          },
          children: [/*#__PURE__*/_jsx(Typography, {
            component: "span",
            variant: "h4",
            sx: {
              fontWeight: function fontWeight(t) {
                return t.typography.fontWeightBold;
              },
              m: 0
            },
            children: /*#__PURE__*/_jsx(Translation, {
              children: function children(t) {
                return /*#__PURE__*/_jsx(DangerouslySetInnerHTML, {
                  body: t(question.text)
                });
              }
            })
          }), /*#__PURE__*/_jsx(ChallengeResponse, {
            answer: answer,
            answerType: answerType
          })]
        }, index);
      })
    })]
  });
};

export default PopoverBody;